<div class="about-view">
  <section class="hero">
    <div class="hero-copy">
      <p class="eyebrow">Built for hiring teams</p>
      <h1>About Evaltrix</h1>
      <p class="intro">Practical, simple, and affordable skill-based hiring for every company.</p>
      <p class="summary">
        We help recruiting and talent teams evaluate real ability without wrestling with complex tools or long set-up
        cycles. Evaltrix keeps the experience lean while still delivering the rigor you need to make confident
        decisions.
      </p>
      <div class="cta-group">
        <a routerLink="/demo" class="btn-primary">Book a demo</a>
        <a routerLink="/contactus" class="btn-secondary" aria-label="Talk to our team">Talk to us</a>
      </div>
    </div>
    <div class="hero-visual" aria-hidden="true">
      <img [src]="heroImageUrl" (error)="onHeroImageError($event)" alt="Evaltrix assessment workspace preview"
        loading="lazy">
    </div>
  </section>

  <section class="narrative">
    <article class="narrative-copy">
      <p>Hiring has become increasingly challenging. Resumes often don’t reflect real ability, many are heavily polished
        or AI-written, and teams handle large volumes of applications where only a small percentage are truly qualified.
        Meanwhile, assessment tools can be complex, expensive, and hard to adopt - especially for smaller or
        fast-growing
        teams.</p>

      <p>Evaltrix was built to solve these everyday hiring challenges in a way that’s simple, efficient, and
        budget-friendly.</p>
    </article>

    <aside class="narrative-highlight">
      <h3>Build better hiring with Evaltrix</h3>
      <p>If you need a straightforward, efficient way to evaluate skills, reduce screening time, and control hiring
        costs
        - without dealing with complex tools - Evaltrix is built for you.</p>
      <p>Have questions or want to learn more? Reach out to us at
        <a href="mailto:support@evaltrix.com" target="_blank" rel="noopener noreferrer" class="email-link"
          (click)="openEmailClient()">support&#64;evaltrix.com</a>
      </p>
    </aside>
  </section>

  <section class="focus-areas">
    <div class="section-heading">
      <p class="eyebrow">Why Evaltrix</p>
      <h2>Designed for the realities of modern hiring</h2>
      <p>Everything we build removes friction for lean recruiting teams - from creating assessments to scaling them with
        confidence.</p>
    </div>

    <!-- Section 1: Mission & Future -->
    <div class="facts-group mission">
      <div class="group-title"><span class="marker"></span>Our Vision & Future</div>
    </div>
    <div class="card-grid mission-grid">
      <article class="card">
        <span class="eyebrow">Our Mission</span>
        <h3>Make skill-based hiring accessible</h3>
        <p>Evaluating real ability shouldn’t require complex software, long setup times, or enterprise-level budgets.
          Every team - large or small - should be able to run reliable assessments with ease.</p>
      </article>

      <article class="card future-card">
        <span class="eyebrow">Looking Ahead</span>
        <h3>The future we’re working toward</h3>
        <ul>
          <li>Companies can create assessments without relying on tech teams</li>
          <li>Tools are simple enough for anyone in HR to use immediately</li>
          <li>High-quality assessments are affordable for every organisation</li>
          <li>Screening becomes faster, clearer, and more skill-driven</li>
        </ul>
      </article>
    </div>

    <!-- Section 2: Details -->
    <div class="facts-group practice">
      <div class="group-title"><span class="marker"></span>Evaltrix: Purpose, Practice, and Principles</div>
    </div>
    <div class="card-grid details-grid">
      <article class="card list-card">
        <span class="eyebrow">What We Do</span>
        <h3>AI that still feels human-friendly</h3>
        <p>Evaltrix helps companies create and run assessments faster through AI-driven tools that are simple to
          operate:</p>
        <ul>
          <li>Generate fresh, role-specific questions instantly</li>
          <li>Add custom instructions to control how questions are created</li>
          <li>Build assessments for any role with adjustable difficulty</li>
          <li>Provide unique question variants to reduce predictability</li>
          <li>Remove the need for technical teams to write assessments</li>
          <li>Keep the interface straightforward and easy for anyone to use</li>
        </ul>
      </article>

      <article class="card list-card">
        <span class="eyebrow">Why We Exist</span>
        <h3>Tackle the hardest parts of screening</h3>
        <p>Modern hiring faces three major issues:</p>
        <ul>
          <li>Resumes don’t reflect real skills - and many are AI-written.</li>
          <li>Most applicants aren’t fully qualified - but still need to be screened.</li>
          <li>Assessment platforms are often expensive and complicated.</li>
        </ul>
        <p>Evaltrix addresses all of these by offering a tool that is simple, affordable, and centred on real skill
          evaluation.</p>
      </article>

      <article class="card list-card compact">
        <span class="eyebrow">Our Approach</span>
        <h3>Principles we build around</h3>
        <ul>
          <li>Easy to use - no training, no complexity, no learning curve</li>
          <li>Skill-first - evaluate real capability, not résumé keywords</li>
          <li>Cost-efficient - accessible to startups, SMEs, and large teams</li>
          <li>Fair and consistent - objective screening for all candidates</li>
        </ul>
        <p>Our goal is to make the early stages of hiring faster and more reliable without overengineering the process.
        </p>
      </article>
    </div>
  </section>

  <section class="cta-band">
    <div class="inner">
      <div>
        <p class="eyebrow">Ready to hire by skills?</p>
        <h3>Launch an assessment in minutes, not weeks.</h3>
      </div>
      <div class="cta-group">
        <a routerLink="/demo" class="btn-primary">Get started</a>
        <a routerLink="/contactus" class="btn-secondary" aria-label="Connect with Evaltrix">Talk to sales</a>
      </div>
    </div>
  </section>
</div>